// Unified "submit order to manufacturer" routing (turbo_sales.md §11 item 5).
// Picks dealer-create (on-platform) vs inventory host handoff (legacy) from partner metadata.

export type OrderManufacturerPartner = {
  id: string;
  name: string;
  /** Active manufacturer_subscriber + target has Turbo Manufacturing entitlement. */
  on_platform: boolean;
};

export type OrderManufacturerPartnerInput = {
  manufacturer_org_id: string;
  name: string;
  has_active_subscriber_relationship: boolean;
  has_turbo_manufacturing_entitlement: boolean;
};

export type ManufacturerSubmissionRoute =
  | { kind: "own_org" }
  | { kind: "dealer_create"; partner: OrderManufacturerPartner }
  | { kind: "legacy_pos_handoff"; partner: OrderManufacturerPartner }
  | { kind: "invalid"; error: string };

export function isManufacturerOnPlatform(input: {
  has_active_subscriber_relationship: boolean;
  has_turbo_manufacturing_entitlement: boolean;
}): boolean {
  return input.has_active_subscriber_relationship && input.has_turbo_manufacturing_entitlement;
}

/** Merge subscriber manufacturers with optional pricing-linked legacy manufacturer. */
export function buildOrderManufacturerPartners(
  subscribers: OrderManufacturerPartnerInput[],
  linkedManufacturer: { id: string; name: string } | null,
): OrderManufacturerPartner[] {
  const byId = new Map<string, OrderManufacturerPartner>();
  for (const row of subscribers) {
    const id = row.manufacturer_org_id.trim();
    if (!id) continue;
    byId.set(id, {
      id,
      name: row.name.trim() || id,
      on_platform: isManufacturerOnPlatform(row),
    });
  }
  if (linkedManufacturer?.id.trim()) {
    const id = linkedManufacturer.id.trim();
    if (!byId.has(id)) {
      byId.set(id, {
        id,
        name: linkedManufacturer.name.trim() || id,
        on_platform: false,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function resolveManufacturerSubmissionRoute(
  destinationManufacturerOrgId: string,
  partners: readonly OrderManufacturerPartner[],
): ManufacturerSubmissionRoute {
  const targetId = destinationManufacturerOrgId.trim();
  if (!targetId) return { kind: "own_org" };
  const partner = partners.find((p) => p.id === targetId);
  if (!partner) {
    return { kind: "invalid", error: "Select a valid manufacturer." };
  }
  if (partner.on_platform) return { kind: "dealer_create", partner };
  return { kind: "legacy_pos_handoff", partner };
}

export function manufacturerSubmissionSuccessCopy(
  route: ManufacturerSubmissionRoute,
): string | null {
  if (route.kind === "own_org" || route.kind === "invalid") return null;
  return `Order submitted to ${route.partner.name} — pending manufacturer review.`;
}
